"use client";

import { cn } from "@/lib/utils";
import { TextStyleKit } from "@tiptap/extension-text-style";
import {
  useEditor,
  EditorContent,
  Editor,
  useEditorState,
  Content,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Button } from "./ui/button";

interface Props {
  defaultValue: Content  | null;
  saveDocumentation: (editor: Editor) => void;
  loadNewContent: (editor: Editor) => void;
}

export const TipTapExtensions = [TextStyleKit, StarterKit];

export default function Tiptap(props: Props) {
  function MenuBar({ editor }: { editor: Editor }) {
    // Read the current editor's state, and re-render the component when it changes
    const editorState = useEditorState({
      editor,
      selector: (ctx) => {
        return {
          isBold: ctx.editor.isActive("bold") ?? false,
          canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
          isItalic: ctx.editor.isActive("italic") ?? false,
          canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
          isStrike: ctx.editor.isActive("strike") ?? false,
          canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
          isCode: ctx.editor.isActive("code") ?? false,
          canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
          canClearMarks:
            ctx.editor.can().chain().unsetAllMarks().run() ?? false,
          isParagraph: ctx.editor.isActive("paragraph") ?? false,
          isHeading1: ctx.editor.isActive("heading", { level: 1 }) ?? false,
          isHeading2: ctx.editor.isActive("heading", { level: 2 }) ?? false,
          isHeading3: ctx.editor.isActive("heading", { level: 3 }) ?? false,
          isHeading4: ctx.editor.isActive("heading", { level: 4 }) ?? false,
          isHeading5: ctx.editor.isActive("heading", { level: 5 }) ?? false,
          isHeading6: ctx.editor.isActive("heading", { level: 6 }) ?? false,
          isBulletList: ctx.editor.isActive("bulletList") ?? false,
          isOrderedList: ctx.editor.isActive("orderedList") ?? false,
          isCodeBlock: ctx.editor.isActive("codeBlock") ?? false,
          isBlockquote: ctx.editor.isActive("blockquote") ?? false,
          canUndo: ctx.editor.can().chain().undo().run() ?? false,
          canRedo: ctx.editor.can().chain().redo().run() ?? false,
        };
      },
    });

    return (
      <div className="control-group dark:bg-black dark:text-white">
        <div className="button-group">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editorState.canBold}
            className={cn(editorState.isBold ? "is-active" : "", "editorStyles")}
          >
            Bold
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editorState.canItalic}
            className={cn(editorState.isItalic ? "is-active" : "", "editorStyles")}
          >
            Italic
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editorState.canStrike}
            className={cn(editorState.isStrike ? "is-active" : "", "editorStyles")}
          >
            Strike
          </button>
          <button
            onClick={() => editor.chain().focus().toggleCode().run()}
            disabled={!editorState.canCode}
            className={cn(editorState.isCode ? "is-active" : "", "editorStyles")}
          >
            Code
          </button>
          <button onClick={() => editor.chain().focus().unsetAllMarks().run()} className={cn(editorState.isCode ? "is-active" : "", "editorStyles")}>
            Clear marks
          </button>
          <button onClick={() => editor.chain().focus().clearNodes().run()} className={cn(editorState.isCode ? "is-active" : "", "editorStyles")}>
            Clear nodes
          </button>
          <button
            onClick={() => editor.chain().focus().setParagraph().run()}
            className={cn(editorState.isParagraph ? "is-active" : "", "editorStyles")}
          >
            Paragraph
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            className={cn(editorState.isHeading1 ? "is-active" : "", "editorStyles")}
          >
            H1
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={cn(editorState.isHeading2 ? "is-active" : "", "editorStyles")}
          >
            H2
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            className={cn(editorState.isHeading3 ? "is-active" : "", "editorStyles")}
          >
            H3
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 4 }).run()
            }
            className={cn(editorState.isHeading4 ? "is-active" : "", "editorStyles")}
          >
            H4
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 5 }).run()
            }
            className={cn(editorState.isHeading5 ? "is-active" : "", "editorStyles")}
          >
            H5
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 6 }).run()
            }
            className={cn(editorState.isHeading6 ? "is-active" : "", "editorStyles")}
          >
            H6
          </button>
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={cn(editorState.isBulletList ? "is-active" : "", "editorStyles")}
          >
            Bullet list
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={cn(editorState.isOrderedList ? "is-active" : "", "editorStyles")}
          >
            Ordered list
          </button>
          <button
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={cn(editorState.isCodeBlock ? "is-active" : "", "editorStyles")}
          >
            Code block
          </button>
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={cn(editorState.isBlockquote ? "is-active" : "", "editorStyles")}
          >
            Blockquote
          </button>
          <button
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className={cn(editorState.isCode ? "is-active" : "", "editorStyles")}
          >
            Horizontal rule
          </button>
          <button onClick={() => editor.chain().focus().setHardBreak().run()}
            className={cn(editorState.isCode ? "is-active" : "", "editorStyles")}
          >
            Hard break
          </button>
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editorState.canUndo}
            className={cn(editorState.isCode ? "is-active" : "", "editorStyles")}
          >
            Undo
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editorState.canRedo}
            className={cn(editorState.isCode ? "is-active" : "", "editorStyles")}
          >
            Redo
          </button>
        </div>
      </div>
    );
  }

  const editor = useEditor({
    extensions: TipTapExtensions, // define your extension array
    content: props.defaultValue ?? ``, // initial content
    onUpdate: ({ editor }) => props.loadNewContent(editor),
  });

  return (
    <> 
        <Button onClick={() => props.saveDocumentation(editor)}>Save documentation</Button>
        <MenuBar editor={editor}/>
        <EditorContent editor={editor} className="m-[1.5rem]" />
    </>
  );
}
